import clsx from 'clsx';
import Button from 'components/Button';
import Overlay from 'components/Layout/Overlay';
import LinkButton from 'components/LinkButton';
import {
    LayoutTypeCornerBased,
    LayoutTypeTopOrBottomBased,
} from 'hooks/useComponentPosition/types';
import useTimer from 'hooks/useTimer';
import {
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {c} from 'ttag';

import {TooltipWrapperContext} from './hook';
import arrow from './TooltipArrow';
import {TOOLTIPTYPE, TooltipWrapperProps} from './types';

type StopPropagation = {
    onClick: (e: {stopPropagation: () => void}) => void;
    onKeyDown: (e: {stopPropagation: () => void}) => void;
    role: 'button';
    tabIndex: 0;
};
const stopPropagation = (): StopPropagation => ({
    onClick: (e: {stopPropagation: () => void}) => e.stopPropagation(),
    onKeyDown: (e: {stopPropagation: () => void}) => e.stopPropagation(),
    role: 'button',
    tabIndex: 0,
});

type TooltipElementProps = {
    closeTooltip?: () => void;
    href?: string;
    layout: LayoutTypeCornerBased | LayoutTypeTopOrBottomBased;
    message?: ReactNode;
};

export const TooltipElement: FC<TooltipElementProps> = ({
    closeTooltip,
    href,
    layout,
    message,
}) => (
    <>
        <div className="flex flex-col gap-2" {...stopPropagation()}>
            {message}
            {href && (
                <div className="flex justify-end gap-2">
                    <Button
                        data-tooltipdisabled={true}
                        onClick={closeTooltip}
                        size="xs"
                    >
                        {c('component').t`Close`}
                    </Button>
                    <LinkButton
                        data-tooltipdisabled={true}
                        href={href}
                        rel="noopener noreferrer"
                        size="xs"
                        target="_blank"
                    >
                        {c('component').t`Learn More`}
                    </LinkButton>
                </div>
            )}
        </div>
        {arrow[layout]}
    </>
);

const TooltipWrapper: FC<TooltipWrapperProps> = ({
    children,
    href,
    layoutMethod = 'top-or-bottom',
    message,
    tooltipType = 'default',
}) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const {clearTimeoutFunction, setTimeoutFunction} = useTimer(
        href ? 1000 : 0,
    );

    useEffect(() => () => {
        clearTimeoutFunction();
    });

    const refs = useRef<
        Record<
            string,
            {
                element: HTMLElement;
                onMouseEnter: () => void;
                onMouseLeave: () => void;
            }
        >
    >({});

    const onMouseEnter = useCallback(
        (id?: string) => {
            clearTimeoutFunction();

            if (!id) return;

            setElement(refs.current[id].element);
        },
        [clearTimeoutFunction],
    );

    const closeTooltip = useCallback(() => {
        clearTimeoutFunction();
        setElement(null);
    }, [clearTimeoutFunction]);

    const onMouseLeave = useCallback(() => {
        setTimeoutFunction(() => closeTooltip());
    }, [closeTooltip, setTimeoutFunction]);

    const setTooltipRef = useCallback<
        (id: string, element: HTMLElement | null) => void
    >(
        (id, targetElement) => {
            //clean up existing/old references
            if (refs.current[id]) {
                refs.current[id].element.removeEventListener(
                    'mouseenter',
                    refs.current[id].onMouseEnter,
                );
                refs.current[id].element.removeEventListener(
                    'mouseleave',
                    refs.current[id].onMouseLeave,
                );
            }

            // If element as passed, add event listeners
            if (targetElement) {
                if (targetElement.dataset.tooltipdisabled) return;
                refs.current[id] = {
                    element: targetElement,
                    onMouseEnter: () => onMouseEnter(id),
                    onMouseLeave: () => onMouseLeave(),
                };

                refs.current[id].element.addEventListener(
                    'mouseenter',
                    refs.current[id].onMouseEnter,
                );
                refs.current[id].element.addEventListener(
                    'mouseleave',
                    refs.current[id].onMouseLeave,
                );
            }
        },
        [onMouseEnter, onMouseLeave],
    );

    const value = useMemo(
        () => ({
            message,
            setTooltipRef,
        }),
        [message, setTooltipRef],
    );

    return (
        <TooltipWrapperContext.Provider value={value}>
            {children}
            {element && (message || href) && (
                <Overlay
                    className={clsx(
                        'break-words',
                        TOOLTIPTYPE[tooltipType],
                        // Linktip
                        !!href && 'linktip',
                    )}
                    element={element}
                    layoutLayer="z-tooltip"
                    layoutMethod={layoutMethod}
                    layoutOffset={6}
                    onMouseEnter={() => onMouseEnter()}
                    onMouseLeave={() => onMouseLeave()}
                >
                    {(layout) => (
                        <TooltipElement
                            closeTooltip={closeTooltip}
                            href={href}
                            layout={layout}
                            message={message}
                        />
                    )}
                </Overlay>
            )}
        </TooltipWrapperContext.Provider>
    );
};

export default TooltipWrapper;
